import { z } from "zod";

// ---- 全社リンク(ブックマーク)
export const companyLinkViewSchema = z.object({
  id: z.string(),
  title: z.string(),
  url: z.string(),
  category: z.string(),
  note: z.string(),
  sortOrder: z.number(),
  active: z.boolean(),
  /** favicon を取得した日時(ISO)。画像のキャッシュの目印。取れていなければ null */
  favicon: z.string().nullable(),
  updatedAt: z.string(),
  revision: z.number(),
});
export type CompanyLinkView = z.infer<typeof companyLinkViewSchema>;
export const companyLinkListResponseSchema = z.object({ links: z.array(companyLinkViewSchema) });
export type CompanyLinkListResponse = z.infer<typeof companyLinkListResponseSchema>;
export const companyLinkResponseSchema = z.object({ link: companyLinkViewSchema });
export type CompanyLinkResponse = z.infer<typeof companyLinkResponseSchema>;

export const createCompanyLinkRequestSchema = z.object({
  title: z.string().trim().min(1, "タイトルは必須です").max(200),
  url: z.string().trim().min(1, "URL は必須です").max(2000),
  category: z.string().trim().max(50).optional(),
  note: z.string().trim().max(500).optional(),
});
export type CreateCompanyLinkRequest = z.infer<typeof createCompanyLinkRequestSchema>;

export const updateCompanyLinkRequestSchema = z.object({
  revision: z.number().int().positive(),
  title: z.string().trim().min(1).max(200).optional(),
  url: z.string().trim().min(1).max(2000).optional(),
  category: z.string().trim().max(50).optional(),
  note: z.string().trim().max(500).optional(),
  active: z.boolean().optional(),
});
export type UpdateCompanyLinkRequest = z.infer<typeof updateCompanyLinkRequestSchema>;

/** 並べ替え(管理画面のドラッグ)。指定した順を先頭にし、残りは今の順のまま後ろに付く。 */
export const reorderCompanyLinksRequestSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(500),
});
export type ReorderCompanyLinksRequest = z.infer<typeof reorderCompanyLinksRequestSchema>;
/** favicon の取り直しの結果(取れた件数・取れなかった件数) */
export const refreshFaviconsResponseSchema = z.object({
  updated: z.number(),
  failed: z.number(),
});
export type RefreshFaviconsResponse = z.infer<typeof refreshFaviconsResponseSchema>;
/** カテゴリの改名。to が既存のカテゴリなら統合 */
export const renameCompanyLinkCategoryRequestSchema = z.object({
  from: z.string().trim().min(1).max(50),
  to: z.string().trim().min(1, "カテゴリ名は必須です").max(50),
});
export type RenameCompanyLinkCategoryRequest = z.infer<
  typeof renameCompanyLinkCategoryRequestSchema
>;

// ---- お知らせ
export const announcementViewSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  publishFrom: z.string(),
  publishUntil: z.string().nullable(),
  pinned: z.boolean(),
  archived: z.boolean(),
  authorName: z.string(),
  updatedAt: z.string(),
  revision: z.number(),
});
export type AnnouncementView = z.infer<typeof announcementViewSchema>;
export const announcementListResponseSchema = z.object({
  announcements: z.array(announcementViewSchema),
});
export type AnnouncementListResponse = z.infer<typeof announcementListResponseSchema>;
export const announcementResponseSchema = z.object({ announcement: announcementViewSchema });
export type AnnouncementResponse = z.infer<typeof announcementResponseSchema>;

export const createAnnouncementRequestSchema = z.object({
  title: z.string().trim().min(1, "タイトルは必須です").max(200),
  body: z.string().max(5000).optional(),
  /** ISO 8601。省略時は今。 */
  publishFrom: z.string().datetime({ offset: true }).optional(),
  publishUntil: z.string().datetime({ offset: true }).nullable().optional(),
  pinned: z.boolean().optional(),
});
export type CreateAnnouncementRequest = z.infer<typeof createAnnouncementRequestSchema>;

export const updateAnnouncementRequestSchema = z.object({
  revision: z.number().int().positive(),
  title: z.string().trim().min(1).max(200).optional(),
  body: z.string().max(5000).optional(),
  publishFrom: z.string().datetime({ offset: true }).optional(),
  publishUntil: z.string().datetime({ offset: true }).nullable().optional(),
  pinned: z.boolean().optional(),
  archived: z.boolean().optional(),
});
export type UpdateAnnouncementRequest = z.infer<typeof updateAnnouncementRequestSchema>;

/** 本人が既読にした、いま掲載中のお知らせの ID(ベルの未読の件数に使う。2026-10-14)。 */
export const announcementReadsResponseSchema = z.object({ readIds: z.array(z.string()) });
export type AnnouncementReadsResponse = z.infer<typeof announcementReadsResponseSchema>;

/** お知らせを既読にする(足すだけ。掲載中でないものは無視する)。 */
export const markAnnouncementsReadRequestSchema = z.object({
  ids: z.array(z.string().min(1).max(100)).max(200),
});
export type MarkAnnouncementsReadRequest = z.infer<typeof markAnnouncementsReadRequestSchema>;
